import { Button, Checkbox, CircularProgress, ComboBox, Select } from '@civilgrid/ui/components';
import { useQuery } from '@tanstack/react-query';
import { uniq, xorBy } from 'lodash';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { printOrgInvoice as apiPrintOrgInvoice, toggleIsBillable as apiToggleIsBillable } from '~/api/internal';
import { getProjects } from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project } from '~/common/types';
import { Notify } from '~/components2/Notify';
import rootStore from '../../../store';

function sortedByCreateDate(projects: Project[]) {
  return [...projects].sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));
}

function formattedMonth({ createdAt }: Project) {
  return `${createdAt.toFormat('MMM')} ${createdAt.toFormat('yyyy')}`;
}

export default function ManageProjectBilling() {
  const { currentOrganization, me } = useSnapshot(rootStore) as typeof rootStore;
  const organizationId = currentOrganization?.id ?? '';

  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['manageProjectBilling', 'projects', organizationId],
    queryFn: () => getProjects(organizationId, false, true),
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const monthPicker = useMemo(() => {
    if (isLoading) {
      return <CircularProgress indeterminate />;
    }
    if (projects.length > 0) {
      const possibleMonths = uniq(sortedByCreateDate(projects).map(formattedMonth));
      setSelectedMonth(possibleMonths[0]);
      return (
        <Select
          label="Month"
          options={[{ label: 'All', value: 'all' }, ...possibleMonths.map((m) => ({ value: m, label: m }))]}
          value={possibleMonths[0]}
          onValueChange={(m) => m && setSelectedMonth(m)}
          data-testid="manage-project-billing-month-select"
        />
      );
    }
    return null;
  }, [projects, isLoading]);

  const projectsInView = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return sortedByCreateDate(projects).filter((p) =>
      selectedMonth === 'all' ? true : formattedMonth(p) === selectedMonth,
    );
  }, [projects, isLoading, selectedMonth]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const projectsTable = useMemo(() => {
    if (isLoading) {
      return <CircularProgress indeterminate />;
    }
    if (projects.length === 0) {
      return <div>No project</div>;
    }
    const rows = projectsInView.map((p) => {
      return (
        <tr key={p.id}>
          <td>
            <Checkbox
              className="block"
              checked={selectedProjects.some((_p) => _p.id === p.id)}
              onCheckedChange={() => setSelectedProjects(xorBy(selectedProjects, [p], 'id'))}
              aria-label={`Select ${p.name}`}
              data-testid={`manage-project-billing-project-checkbox-${p.id}`}
            />
          </td>
          <td>
            <a
              href={`/projects/${p.id}/billing`}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-50 underline hover:text-blue-60"
            >
              {p.projectId}
            </a>
          </td>
          <td>{p.createdAt.toLocaleString(DateTime.DATE_MED)}</td>
          <td>{p.name}</td>
          <td>{p.status}</td>
          <td>
            <Checkbox
              className="block"
              defaultChecked={p.isBillable ?? true}
              aria-label={`${p.name} is billable`}
              data-testid={`manage-project-billing-billable-checkbox-${p.id}`}
              onCheckedChange={async () => {
                await apiToggleIsBillable(p.id);
              }}
            />
          </td>
        </tr>
      );
    });
    return (
      <table className="w-full text-left">
        <thead>
          <tr>
            <th>
              <Checkbox
                className="block"
                checked={selectedProjects.length === projectsInView.length}
                aria-label="Select all projects"
                onCheckedChange={(checked) => {
                  checked ? setSelectedProjects([...projectsInView]) : setSelectedProjects([]);
                }}
                data-testid="manage-project-billing-select-all-checkbox"
              />
            </th>
            <th>Project ID</th>
            <th>Created on</th>
            <th className="text-left">Name</th>
            <th className="text-left">Status</th>
            <th>Is Billable</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    );
  }, [projectsInView, selectedProjects.length, selectedMonth, isLoading, projects.length]);

  return (
    <div>
      <div className="mb-4 flex items-end space-x-10">
        <div className="w-48">
          <ComboBox
            label="Organization"
            options={me?.organizations.map((o) => ({ value: o.id, label: o.name })) ?? []}
            value={currentOrganization?.id ?? ''}
            onValueChange={(id) => id && rootStore.setCurrentOrganization(id)}
            allowDeselect={false}
            placeholder="Select organization"
            searchPlaceholder="Search organizations..."
            emptyText="No organization found."
            data-testid="manage-project-billing-organization-combobox"
          />
        </div>
        <div className="min-w-[120px]">{monthPicker}</div>

        <Button
          disabled={selectedProjects.length === 0}
          data-testid="manage-project-billing-print-button"
          onClick={async () => {
            const defaultRange = `${
              selectedProjects[selectedProjects.length - 1].startDate
                ?.startOf('month')
                ?.toLocaleString(DateTime.DATE_SHORT) ||
              selectedProjects[selectedProjects.length - 1].createdAt
                ?.startOf('month')
                ?.toLocaleString(DateTime.DATE_SHORT)
            } - ${DateTime.now().toLocaleString(DateTime.DATE_SHORT)}`;

            const dateRange = prompt(
              'Date range displayed in receipt. Default is <start of month of oldest project - todays date>',
              defaultRange,
            );
            if (dateRange) {
              const projectIds = [...selectedProjects.map((p) => p.id)].reverse();
              try {
                await apiPrintOrgInvoice(projectIds, dateRange);
                Notify.info({
                  message: 'Transaction log packet will be emailed.',
                });
                captureTypedPosthogEvent(POSTHOG_EVENTS.organizationInvoicePrinted, {
                  organization_id: organizationId,
                  project_ids: projectIds,
                });
              } catch (err) {
                const reason = getReasonForError(err);
                captureTypedPosthogEvent(POSTHOG_EVENTS.organizationInvoicePrintFailed, {
                  organization_id: organizationId,
                  project_ids: projectIds,
                  reason,
                });
                Notify.error({
                  message:
                    'There was an error printing the transaction log. Please try again later or contact support.',
                });
              }
            }
          }}
        >
          Print Transaction Log
        </Button>
      </div>
      <div className="m-2 border border-slate-500 border-opacity-10" />
      {projectsTable}
      <div className="mt-8 italic">
        Note: If a project contains no transactions, it will not be included in the summary
      </div>
    </div>
  );
}
